/**
 * Offer Metrics Helper
 * 
 * Centralized helper for all offer-related business logic and calculations.
 * This module provides consistent offer metrics throughout the application.
 * 
 * Features:
 * - Offer statistics and counts
 * - Value calculations per offer
 * - Conversion tracking (offer to order)
 * - Performance metrics and analytics
 * 
 * Usage:
 * import offerMetrics from '@/helper/offerMetrics'
 * const { getActiveOffers, getOfferTotalValue } = offerMetrics()
 */

import store from '@/store'
import moment from 'moment'

export default function offerMetrics() {
  // Get all offers from store
  function getOffers() {
    return store.getters.offerEntries || []
  }

  // Get all orders from store (for conversion metrics)
  function getOrders() {
    return store.getters.orderEntries || []
  }

  // Basic Offer Counts
  function getOfferCount() {
    return getOffers().length
  }

  function getActiveOfferCount() {
    return getOffers().filter(offer => offer.isActive === true).length
  }

  function getInactiveOfferCount() {
    return getOffers().filter(offer => offer.isActive !== true).length
  }

  // Offer Value Calculations
  function getOfferTotalValue(offer) {
    if (!offer || !offer.storages) return 0
    return offer.storages.reduce((sum, storage) => sum + (storage.price || 0), 0)
  }

  function getAllOffersValue() {
    return getOffers().reduce((total, offer) => total + getOfferTotalValue(offer), 0)
  }

  function getActiveOffersValue() {
    return getOffers()
      .filter(offer => offer.isActive === true)
      .reduce((total, offer) => total + getOfferTotalValue(offer), 0)
  }

  function getAverageOfferValue() {
    const offers = getOffers()
    if (offers.length === 0) return 0
    return Math.round(getAllOffersValue() / offers.length)
  }

  // Offer Discovery
  function getNewestOffer() {
    const offers = getOffers()
    if (offers.length === 0) return null

    return [...offers].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )[0]
  }

  function getHighestValueOffer() {
    const offers = getOffers()
    if (offers.length === 0) return null

    return offers.reduce((maxOffer, offer) => {
      const offerValue = getOfferTotalValue(offer)
      const maxValue = getOfferTotalValue(maxOffer)
      return offerValue > maxValue ? offer : maxOffer
    }, offers[0])
  }

  function getTopOffersByValue(limit = 5) {
    const offers = getOffers()
    
    const offersWithValue = offers.map(offer => ({
      ...offer,
      totalValue: getOfferTotalValue(offer)
    }))

    return offersWithValue
      .sort((a, b) => b.totalValue - a.totalValue)
      .slice(0, limit)
  }

  // Conversion Metrics
  function getOfferToOrderConversionRate() {
    const offers = getOffers()

    if (offers.length === 0) return 0

    // Count offers that have been converted to orders using direct bookingCompleted field
    const convertedOffers = offers.filter(offer => offer.bookingCompleted === true)

    return Math.round((convertedOffers.length / offers.length) * 100)
  }

  // Time-based Analytics
  function getOffersByPeriod(period = 'month') {
    const offers = getOffers()
    const now = moment()
    let startDate

    switch (period) {
      case 'week':
        startDate = moment().subtract(1, 'week')
        break
      case 'month':
        startDate = moment().subtract(1, 'month')
        break
      case 'quarter':
        startDate = moment().subtract(3, 'months')
        break
      case 'year':
        startDate = moment().subtract(1, 'year')
        break
      default:
        startDate = moment().subtract(1, 'month')
    }

    return offers.filter(offer => {
      const offerDate = moment(offer.created_at)
      return offerDate.isBetween(startDate, now, null, '[]')
    })
  }

  function getOffersExpiringWithin(days = 7) {
    const offers = getOffers().filter(offer => offer.isActive === true)
    const futureDate = moment().add(days, 'days')
    
    return offers.filter(offer => {
      if (!offer.validUntil && !offer.expiryDate) return false
      const expiryDate = moment(offer.validUntil || offer.expiryDate)
      return expiryDate.isBetween(moment(), futureDate, null, '[]')
    })
  }

  function getOfferTrend(months = 12) {
    const offers = getOffers()
    const trendData = {}

    for (let i = months - 1; i >= 0; i -= 1) {
      const date = moment().subtract(i, 'months')
      const monthKey = date.format('YYYY-MM')
      const monthLabel = date.format('MMM', 'de')

      const offersInMonth = offers.filter(offer => {
        const offerDate = moment(offer.created_at)
        return offerDate.format('YYYY-MM') === monthKey
      })

      trendData[monthLabel] = {
        count: offersInMonth.length,
        value: offersInMonth.reduce((sum, offer) => sum + getOfferTotalValue(offer), 0),
        month: monthKey,
        label: monthLabel
      }
    }

    return trendData
  }

  // Storage Metrics within Offers
  function getAverageStoragesPerOffer() {
    const offers = getOffers()
    if (offers.length === 0) return 0
    
    const totalStorages = offers.reduce(
      (sum, offer) => sum + (offer.storages ? offer.storages.length : 0), 
      0
    )
    
    return Math.round((totalStorages / offers.length) * 10) / 10
  }

  function getMostCommonStorageSize() {
    const offers = getOffers()
    const storageSizes = {}
    
    offers.forEach(offer => {
      if (offer.storages) {
        offer.storages.forEach(storage => {
          const size = storage.size || storage.type || 'Unknown'
          storageSizes[size] = (storageSizes[size] || 0) + 1
        })
      }
    })
    
    const sortedSizes = Object.entries(storageSizes)
      .sort((a, b) => b[1] - a[1])
    
    return sortedSizes.length > 0 ? sortedSizes[0][0] : null
  }

  // Customer Analysis
  function getOffersByCustomer(customerId) {
    return getOffers().filter(offer => 
      offer.customerId === customerId 
      || offer.customer_id === customerId
      || (offer.customer && offer.customer.id === customerId))
  }

  function getCustomersWithActiveOffers() {
    const activeOffers = getOffers().filter(offer => offer.isActive === true)
    const uniqueCustomers = new Set()
    
    activeOffers.forEach(offer => {
      if (offer.customerId) uniqueCustomers.add(offer.customerId)
      if (offer.customer_id) uniqueCustomers.add(offer.customer_id)
      if (offer.customer && offer.customer.id) uniqueCustomers.add(offer.customer.id)
    })
    
    return uniqueCustomers.size
  }

  // Search and Filtering
  function searchOffers(query) {
    if (!query || query.trim() === '') return getOffers()

    const searchTerm = query.toLowerCase().trim()
    return getOffers().filter(offer => {
      const firstName = (offer.firstName || '').toLowerCase()
      const lastName = (offer.lastName || '').toLowerCase()
      const email = (offer.email || '').toLowerCase()
      const phone = (offer.phoneNumber || '').toLowerCase()
      const id = String(offer.id || '')

      return firstName.includes(searchTerm)
             || lastName.includes(searchTerm)
             || email.includes(searchTerm)
             || phone.includes(searchTerm)
             || id.includes(searchTerm)
             || `${firstName} ${lastName}`.includes(searchTerm)
    })
  }

  function filterOffersByStatus(status) {
    const offers = getOffers()
    
    switch (status) {
      case 'active':
        return offers.filter(o => o.isActive === true)
      case 'inactive':
        return offers.filter(o => o.isActive !== true)
      case 'expiring':
        return getOffersExpiringWithin(7)
      default:
        return offers
    }
  }

  function filterOffersByValueRange(minValue = 0, maxValue = Infinity) {
    return getOffers().filter(offer => {
      const value = getOfferTotalValue(offer)
      return value >= minValue && value <= maxValue
    })
  }

  // Performance Metrics
  function getOfferResponseTime() {
    // Calculate average time between inquiry and offer creation
    // This would require inquiry data relationship
    // Placeholder for now
    return 'N/A'
  }

  function getOfferAcceptanceRate() {
    const offers = getOffers()
    if (offers.length === 0) return 0
    
    // Count offers that have been accepted
    // This would need an 'accepted' field or status
    const acceptedOffers = offers.filter(offer => 
      offer.status === 'accepted' 
      || offer.isAccepted === true
      || offer.accepted === true)
    
    return Math.round((acceptedOffers.length / offers.length) * 100)
  }

  // Summary Statistics
  function getOfferSummaryStats() {
    const offers = getOffers()
    const activeOffers = offers.filter(o => o.isActive === true)
    const totalValue = getAllOffersValue()
    const activeValue = getActiveOffersValue()
    
    return {
      total: offers.length,
      active: activeOffers.length,
      inactive: offers.length - activeOffers.length,
      totalValue,
      activeValue,
      averageValue: getAverageOfferValue(),
      conversionRate: getOfferToOrderConversionRate(),
      expiringCount: getOffersExpiringWithin(7).length,
      customersWithOffers: getCustomersWithActiveOffers()
    }
  }

  // Export all functions
  return {
    // Basic counts
    getOfferCount,
    getActiveOfferCount,
    getInactiveOfferCount,
    
    // Value calculations
    getOfferTotalValue,
    getAllOffersValue,
    getActiveOffersValue,
    getAverageOfferValue,
    
    // Discovery
    getNewestOffer,
    getHighestValueOffer,
    getTopOffersByValue,
    
    // Conversion metrics
    getOfferToOrderConversionRate,
    
    // Time-based analytics
    getOffersByPeriod,
    getOffersExpiringWithin,
    getOfferTrend,
    
    // Storage metrics
    getAverageStoragesPerOffer,
    getMostCommonStorageSize,
    
    // Customer analysis
    getOffersByCustomer,
    getCustomersWithActiveOffers,
    
    // Search and filtering
    searchOffers,
    filterOffersByStatus,
    filterOffersByValueRange,
    
    // Performance metrics
    getOfferResponseTime,
    getOfferAcceptanceRate,
    
    // Summary
    getOfferSummaryStats,
    
    // Raw data access
    getOffers,
    getOrders
  }
}
